/**
 * Dialog zum Entfernen einzelner Urlaubstage aus einem Antrag.
 *
 * Listet alle Werktage des Urlaubszeitraums (ohne Wochenenden/Feiertage) auf.
 * Der Nutzer markiert die zu entfernenden Tage; beim Bestätigen wird
 * `vacationsAPI.removeDays` aufgerufen. Tage in der Mitte führen serverseitig
 * automatisch zu einer Aufteilung in mehrere Zeiträume.
 */
import React, { useMemo, useState } from 'react';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, CalendarX, AlertTriangle } from 'lucide-react';
import { vacationsAPI } from '../../lib/api';
import { toast } from 'sonner';

export function RemoveDaysDialog({ open, onOpenChange, vacation, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Werktage des Zeitraums (ohne Wochenende + ohne gespeicherte Feiertage)
  const businessDays = useMemo(() => {
    if (!vacation) return [];
    const excluded = new Set(vacation.excluded_holidays || []);
    try {
      const days = eachDayOfInterval({
        start: parseISO(vacation.start_date),
        end: parseISO(vacation.end_date),
      });
      return days
        .map((d) => ({ iso: format(d, 'yyyy-MM-dd'), date: d }))
        .filter(({ iso, date }) => date.getDay() !== 0 && date.getDay() !== 6 && !excluded.has(iso));
    } catch {
      return [];
    }
  }, [vacation]);

  const toggle = (iso) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  };

  const removeAll = selected.size > 0 && selected.size === businessDays.length;

  const handleConfirm = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      const res = await vacationsAPI.removeDays(vacation.id, Array.from(selected));
      const segments = res.data?.segments ?? 0;
      if (segments === 0) {
        toast.success(vacation.status === 'approved' ? 'Alle Tage entfernt – Urlaub storniert' : 'Alle Tage entfernt – Antrag gelöscht');
      } else if (segments > 1) {
        toast.success(`Tage entfernt – Urlaub in ${segments} Zeiträume aufgeteilt`);
      } else {
        toast.success('Urlaubstage entfernt');
      }
      onOpenChange(false);
      setSelected(new Set());
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Entfernen der Tage');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) { onOpenChange(o); if (!o) setSelected(new Set()); } }}>
      <DialogContent className="max-w-md" data-testid="remove-days-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarX className="h-5 w-5 text-primary" />
            Einzelne Tage entfernen
          </DialogTitle>
          <DialogDescription>
            {vacation && (
              <>
                Urlaub {format(parseISO(vacation.start_date), 'dd.MM.', { locale: de })}
                {' – '}
                {format(parseISO(vacation.end_date), 'dd.MM.yyyy', { locale: de })}.
                Wähle die Tage, die entfernt werden sollen. Tage in der Mitte teilen den Urlaub automatisch auf.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-[320px] overflow-y-auto py-1">
          {businessDays.map(({ iso, date }) => (
            <label
              key={iso}
              className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-secondary/50 cursor-pointer"
              data-testid={`remove-day-${iso}`}
            >
              <Checkbox
                checked={selected.has(iso)}
                onCheckedChange={() => toggle(iso)}
                data-testid={`remove-day-checkbox-${iso}`}
              />
              <span className={`text-sm ${selected.has(iso) ? 'line-through text-muted-foreground' : ''}`}>
                {format(date, 'EEEE, dd.MM.yyyy', { locale: de })}
              </span>
            </label>
          ))}
          {businessDays.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">Keine entfernbaren Werktage gefunden.</p>
          )}
        </div>

        {removeAll && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-700 p-3" data-testid="remove-all-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Es werden <strong>alle</strong> Tage entfernt – der Urlaub wird damit {vacation?.status === 'approved' ? 'storniert' : 'gelöscht'}.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving} data-testid="remove-days-cancel">
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            className="rounded-full"
            onClick={handleConfirm}
            disabled={saving || selected.size === 0}
            data-testid="remove-days-confirm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `${selected.size} Tag${selected.size === 1 ? '' : 'e'} entfernen`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RemoveDaysDialog;
